import React, {useContext, useEffect, useState} from 'react'; // every page needs to import react

// if you want to interact with the database
import 'firebase/compat/firestore';
import {
  FaChevronLeft,
  FaFileAlt,
  FaFolderOpen,
  FaFlag,
  FaChevronRight,
  FaShoePrints,
  FaComments,
  FaQuestionCircle,
  FaCog,
  FaHatWizard,
  FaGlasses,
  FaComment,
  FaKey,
  FaFlask,
} from 'react-icons/fa';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import AssnPrompt from './AssnPrompt/AssnPrompt';
import FileTree from './Files/FileTree';
import {usePromptPath, getSolnPath} from '../utils/general';
import {DocsSplash} from './LeftCols/DocsSplash';
import {StepperView} from './LeftCols/StepperView';
import {IDEContext} from './contexts/IDEContext';
import {checkIsProjectKarel} from './utils/general';
import {StyleFeedbackTab} from '../course/styleFeedback/StyleFeedbackTab';

import {SettingsView} from './LeftCols/Settings';
import {ProjectKeysSplash} from './ProjectKeys/ProjectKeysSplash';
import {CourseContext} from 'contexts/CourseContext';
import {inferShowSolutionBehavior} from '../assignments/models/assignmentMetadata';
import {CompletionContext} from 'contexts/CompletionContext';
import { CollabContext } from './sync/CollabContext';
import { Loading } from 'components/loading/Loading';
import { useCourseId } from 'hooks/router/useUrlParams';
import { ProfileContext } from 'contexts/ProfileContext';
import { isMinimumRole } from '../contexts/profileUtil';
import { Role } from 'types/role';

// Making the page a variable here is problematic because you
// have to create the prompt page before you know what the assnId.
const NAV_STATES = [
  {
    name: 'TeachNow Chat',
    icon: <FaComments size={20} />,
  },
  {
    name: 'Your Task',
    icon: <FaFlag size={18} />,
  },
  {
    name: 'Files',
    icon: <FaFolderOpen size={20} />,
  },
  {
    name: 'Replay',
    icon: <FaShoePrints size={20} />,
  },
  {
    name: 'Style Feedback',
    icon: <FaGlasses size={20} />,
  },
  {
    name: 'Feedback',
    icon: <FaComment size={20} />,
  },
];

interface PageProps {
  selectedTab: string;
  promptPath: string;
  solnPath: string;
  projectData: any;
  assnData: any;
  revertToStarterFn: () => Promise<void>;
  onChangeFile: any;
  currFile: any;
  fileStructure: any;
  setFileStructure: any;
  hasAssn: boolean;
  onFileCodeUpdate: any;
  isKarel: boolean;
}

const Page = ({
  selectedTab,
  promptPath,
  solnPath,
  projectData,
  assnData,
  revertToStarterFn,
  onChangeFile,
  currFile,
  fileStructure,
  setFileStructure,
  hasAssn,
  onFileCodeUpdate,
  isKarel,
}: PageProps) => {
  const {isAssnCompleted} = useContext(CompletionContext);
  if (selectedTab === 'Your Task') {
    let title = assnData?.metaData?.title ?? projectData.title;
    const showSolutionBehavior = inferShowSolutionBehavior(assnData?.metaData);
    const assignmentId = assnData?.metaData?.uid ?? projectData.uid;
    const isAssignmentCompleted = isAssnCompleted(assignmentId);
    return (
      <AssnPrompt
        promptPath={promptPath}
        solnPath={solnPath}
        assnTitle={title}
        showSolutionBehavior={showSolutionBehavior}
        isAssignmentCompleted={isAssignmentCompleted}
        revertToStarterFn={revertToStarterFn}
      />
    );
  }
  if (selectedTab === 'Files') {
    return (
      <>
        <LeftColTitle title="Project Files" />
        <FileTree
          projectId={projectData.uid}
          fileStructure={fileStructure}
          setFileStructure={setFileStructure}
          onChangeFile={onChangeFile}
          currFile={currFile}
          isCreative={!hasAssn}
          onFileCodeUpdate={onFileCodeUpdate}
        />
      </>
    );
  }
  if (selectedTab === 'Docs') {
    return <DocsSplash />;
  }
  if (selectedTab === 'Replay') {
    return <StepperView />;
  }
  if (selectedTab === 'Settings') {
    return <SettingsView />;
  }
  if (selectedTab === 'Style Feedback') {
    return (
      <StyleFeedbackTab
        projectData={projectData}
        fileStructure={fileStructure}
      />
    );
  }
  if (selectedTab === 'Keys') {
    return <ProjectKeysSplash />;
  }
  return <LeftColTitle title="Coming Soon!" />;
};

export const LeftCol = ({
  isMinimized,
  setMinimizedState,
  projectData,
  assnData,
  revertToStarterFn,
  currFile,
  onChangeFile,
  fileStructure,
  setFileStructure,
  onFileCodeUpdate,
}) => {
  const {promptPath, isPromptPathLoading, promptPathError} = usePromptPath(projectData);
  const solnPath = getSolnPath(projectData);
  const {setSelectedTab, selectedTab} = useContext(IDEContext);
  const {roomLoaded } = useContext(CollabContext);
  const hasAssn = !!promptPath;

  const {stepMode} = useContext(IDEContext);
  const isKarel = checkIsProjectKarel(projectData, assnData);

  useEffect(() => {
    const startIndex = roomLoaded ? 0 : hasAssn ? 1 : 2; // used to add chat as default for meetings
    setSelectedTab(NAV_STATES[startIndex].name);
  }, [hasAssn]);

  useEffect(() => {
    if (stepMode && !isKarel) {
      setSelectedTab('Replay');
    }
  }, [stepMode]);

  if (isPromptPathLoading) {
    return <Loading />;
  }

  if (promptPathError) {
    console.error('Error getting prompt path', promptPathError);
  }

  return (
    <div
      className="d-flex flex-row h-100"
      style={{
        backgroundColor: '#f1f1f1',
      }}
      id="ide-left-col"
    >
      <SideBar
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        isMinimized={isMinimized}
        setMinimizedState={setMinimizedState}
        promptPath={promptPath}
        isMeeting={roomLoaded}
      />

      <div
        className="d-flex flex-column w-100"
        style={{
          backgroundColor: "#f1f1f1",
          height: 'calc(100vh - 60px)',
          overflow: 'hidden',
          position: 'relative',
        }}
        id="ide-main-content"
      >
        <Page
          promptPath={promptPath}
          solnPath={solnPath}
          selectedTab={selectedTab}
          projectData={projectData}
          onChangeFile={onChangeFile}
          currFile={currFile}
          fileStructure={fileStructure}
          setFileStructure={setFileStructure}
          assnData={assnData}
          revertToStarterFn={revertToStarterFn}
          hasAssn={hasAssn}
          onFileCodeUpdate={onFileCodeUpdate}
          isKarel={isKarel}
        />
      </div>
    </div>
  );
};

const SideBar = ({
  selectedTab,
  setSelectedTab,
  isMinimized,
  setMinimizedState,
  promptPath,
  isMeeting,
}) => {
  const hasAssn = promptPath != undefined;
  const {setHelpMode, assnData, projectData, isDiagnostic, isKarel} =
    useContext(IDEContext);
  const {isFeatureEnabled} = useContext(CourseContext);


  const handleKeyPress = (event, func) => {
    if (event.key === 'Enter') {
      // Simulate a click event
      func();
    }
  };

  return (
    <div
      className="pt-0 d-grid flex-column justify-space-between ide-side-bar border-end align-content-between h-100"
      id="ide-side-bar-inner"
      style={{
        backgroundColor: "#272727",
      }}
    >
      {/* <MinimizeButton {...{ isMinimized, setMinimizedState }} /> */}

      <div className="d-flex flex-column">
        {NAV_STATES.map(item => {
          if (
            (item.name === 'Your Task' && !hasAssn) ||
            (item.name === 'Style Feedback' && (isKarel || !assnData)) ||
            (item.name === 'TeachNow Chat' && !isMeeting) ||
            (item.name === 'Replay' && (isKarel || isDiagnostic)) ||
            (item.name === 'Feedback' && !isFeatureEnabled('grades'))
          ) {
            return <span key={item.name}></span>; // empty span render
          }

          return (
            <NavIcon
              isMinimized={isMinimized}
              setMinimizedState={setMinimizedState}
              key={item.name}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              state={item.name}
              icon={item.icon}
              id={item.name + '_sidebar_icon'}
              onKeyPress={handleKeyPress}
            />
          );
        })}
      </div>
      <div className="d-flex flex-column align-content-center justify-center">
      {(!isDiagnostic && !isKarel) && (
          <OverlayTrigger
            placement="right"
            delay={{show: 100, hide: 100}}
            overlay={props => (
              <Tooltip id="button-tooltip" {...props}>
                Project Keys
              </Tooltip>
            )}
          >
            <div
              onClick={() => {
                setSelectedTab('Keys');
              }}
              onKeyDown={e =>
                handleKeyPress(e, () => {
                  setSelectedTab('Keys');
                })
              }
              role="button"
              className={`btn radius mt-2`}
              tabIndex={0}
              aria-label="Project Keys"
            >
              <FaKey size={24} />
            </div>
          </OverlayTrigger>
        )}
        {!isDiagnostic && (
          <NavIcon
            isMinimized={isMinimized}
            setMinimizedState={setMinimizedState}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            state={"Settings"}
            icon={<FaCog size={24} />}
            id={"Settings" + '_sidebar_icon'}
            onKeyPress={handleKeyPress}
          />
        )}
        <OverlayTrigger
          placement="right"
          delay={{show: 100, hide: 100}}
          overlay={props => (
            <Tooltip id="button-tooltip" {...props}>
              IDE Help
            </Tooltip>
          )}
        >
          <div
            onClick={() => {
              setHelpMode(true);
            }}
            onKeyDown={e =>
              handleKeyPress(e, () => {
                setHelpMode(true);
              })
            }
            role="button"
            className={`btn radius ide-nav-unselected`}
            id="help-btn"
            aria-label="IDE Help"
            tabIndex={0}
          >
            <FaQuestionCircle size={24} />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};
const NavIcon = ({
  icon,
  state,
  selectedTab,
  setSelectedTab,
  isMinimized,
  setMinimizedState,
  id,
  onKeyPress,
}) => {

  // the nav is selected if...
  const isSelected = selectedTab === state;

  // decide if the button is selected
  let buttonSelectionClass = 'ide-nav-unselected';
  if (!isMinimized && isSelected) {
    buttonSelectionClass = 'ide-nav-selected';
  }

  const onClick = () => {
    if (isMinimized) {
      setMinimizedState('standard');
    }
    if (isSelected && !isMinimized) {
      setMinimizedState('minimized');
    }
    setSelectedTab(state);

  };


  return (
    <OverlayTrigger
      placement="right"
      delay={{show: 100, hide: 100}}
      overlay={props => (
        <Tooltip id="button-tooltip" {...props}>
          {state}
        </Tooltip>
      )}
    >
      <div
        id={id}
        role="button"
        onClick={() => onClick()}
        className={`btn radius ` + buttonSelectionClass}
        tabIndex={0}
        aria-label={state}
        onKeyDown={e => onKeyPress(e, onClick)}
      >
        {icon}
      </div>
    </OverlayTrigger>
  );
};
const MinimizeButton = ({isMinimized, setMinimizedState}) => {
  // if you hit the button, we will set the state to this value
  const toggleState = isMinimized ? 'standard' : 'minimized';
  return (
    <OverlayTrigger
      placement="right"
      delay={{show: 100, hide: 100}}
      overlay={props => (
        <Tooltip id="button-tooltip" {...props}>
          {isMinimized ? 'Show Panel' : 'Hide Panel'}
        </Tooltip>
      )}
    >
      <button
        className="btn"
        style={{
          padding: '12px 11px 12px 11px',
        }}
        onClick={() => setMinimizedState(toggleState)}
      >
        {getMinimizeIcon(isMinimized)}
      </button>
    </OverlayTrigger>
  );
};

const getMinimizeIcon = isMinimized => {
  if (isMinimized) {
    return <FaChevronRight size={20} />;
  }
  return <FaChevronLeft size={20} />;
};

export const LeftColTitle = ({title}) => {
  return (
    <div
      className="w-100"
      id="left-col-title"
      style={{
        paddingLeft: '10px',
        paddingRight: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '10px',
      }}
    >
      <span className="ideHeadingLight">{title}</span>
    </div>
  );
};
